import React from "react";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import type { AdminPermission } from "@config/permissions";

export const PERMISSION_ERROR_PATH = "/admin/permission-error";

interface PermissionGuardProps {
    /** User must have ALL listed permissions */
    requires?: AdminPermission | AdminPermission[];
    /** User must have ANY of the listed permissions */
    requiresAny?: AdminPermission[];
    /** Override the redirect target (defaults to PERMISSION_ERROR_PATH) */
    fallback?: ReactNode;
    children: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
    requires,
    requiresAny,
    fallback,
    children,
}) => {
    const { hasPermission } = useAdminPermissions();
    const location = useLocation();

    const allGranted = requires
        ? (Array.isArray(requires) ? requires : [requires]).every(hasPermission)
        : true;

    const anyGranted = requiresAny
        ? requiresAny.some(hasPermission)
        : true;

    if (!allGranted || !anyGranted) {
        if (fallback !== undefined) return <>{fallback}</>;
        return (
            <Navigate
                to={PERMISSION_ERROR_PATH}
                state={{ from: location.pathname }}
                replace
            />
        );
    }

    return <>{children}</>;
};
